import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";
import { cn } from "@/lib/utils";

type Variant = "hero" | "purple" | "teal" | "sunset";

const variantClass: Record<Variant, string> = {
  hero: "mkt-wash-peach",
  purple: "mkt-wash-lilac",
  teal: "mkt-wash-sky",
  sunset: "mkt-wash-peach",
};


export const FeatureSection = ({
  eyebrow,
  title,
  description,
  cta = "Start For Free",
  ctaHref = "/auth?mode=signup",
  variant = "hero",
  mockup,
  reverse = false,
  proofChip,
}: {
  eyebrow?: string;
  title: ReactNode;
  description: string;
  cta?: string;
  ctaHref?: string;
  variant?: Variant;
  mockup: ReactNode;
  reverse?: boolean;
  proofChip?: string;
}) => {
  const navigate = useNavigate();
  return (
    <section className="container mx-auto px-3 lg:px-8 py-10 sm:py-14 lg:py-20">
      <div
        className={cn(
          "grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center",
          reverse && "lg:[&>*:first-child]:order-2"
        )}
      >
        {/* Copy column */}
        <div className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
          {eyebrow && (
            <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
              {eyebrow}
            </div>
          )}
          <h2 className="text-3xl sm:text-[44px] lg:text-[52px] tracking-[-0.02em] text-foreground leading-[1.08]">
            {title}
          </h2>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
          {proofChip && (
            <div className="mt-5 inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-secondary text-foreground text-[11px] sm:text-xs font-semibold leading-tight">
              {proofChip}
            </div>
          )}
          <div className="mt-6 sm:mt-7 hidden sm:flex justify-center lg:justify-start">
            <PillButton variant="primary" size="lg" onClick={() => navigate(ctaHref)}>
              {cta}
            </PillButton>
          </div>
        </div>

        {/* Visual column */}
        <div
          className={cn(
            "relative rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden border border-border/70 p-4 sm:p-8 lg:p-10",
            variantClass[variant]
          )}
        >
          <div className="absolute -right-20 top-10 w-[360px] h-[360px] rounded-full bg-white/40 blur-3xl pointer-events-none" />
          <div className="relative">{mockup}</div>
        </div>
      </div>
    </section>
  );

};
